'use client';

import { forwardRef, useContext } from 'react';

import { ProgressBar } from '../progress-bar';
import { ProgressStepperRootContext } from './context/progress-stepper-root.context';
import { progressStepperBarRecipe } from './styles/progress-stepper-bar.styles';
import type { ProgressStepperBarPropsInterface } from './types/progress-stepper-bar.types';

/**
 * ProgressStepper.Bar
 *
 * @related ['Progress']
 * @since 10.0.0
 */
export const ProgressStepperBar = forwardRef<HTMLDivElement, ProgressStepperBarPropsInterface>(
  (props: ProgressStepperBarPropsInterface, forwardedRef) => {
    const {
      'aria-label': ariaLabel = 'progress',
      'aria-valuetext': ariaValueText = '',
      'css': cssProp,
      ...rest
    } = props;
    const { activeStepIndex, orientation, steps } = useContext(ProgressStepperRootContext);

    const stepProgressText =
      ariaValueText ||
      (() => {
        if (activeStepIndex < 0) return 'No steps completed';
        if (activeStepIndex >= steps.length) return 'All steps completed';
        return `Step ${activeStepIndex + 1} of ${steps.length}`;
      })();

    return (
      <ProgressBar
        aria-label={ariaLabel}
        aria-valuetext={stepProgressText}
        value={activeStepIndex + 1}
        minValue={0}
        maxValue={steps.length}
        ref={forwardedRef}
        css={{ ...progressStepperBarRecipe.raw({ orientation }), ...cssProp }}
        data-c11n-component="ProgressStepper.Bar"
        data-c11n-version={__C11N_VERSION__}
        stepperOffset
        {...rest}
      />
    );
  },
);

ProgressStepperBar.displayName = 'ProgressStepper.Bar';
